import { useEffect, useContext, useState } from 'react'
import { TableContext } from 'Context/Table'
import { useRequestStore } from 'Store/Request'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useSteelPlatesStore } from 'Store/RequestSteelPlates'
import { useAuthStore } from 'Store/Auth'
import { useNavigate } from 'react-router-dom'

import CustomActionCell from './Components/ActionCell'
import StatusCell from './Components/StatusCell'

import { isMobile } from 'react-device-detect'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'
import { IssueType, IssueTypeTranslated } from 'Configuration/Paco'
import Swal from 'sweetalert2'
import moment from 'moment'
import 'moment-timezone'

const INITIAL_FILTER = {
  page: 1,
  pageSize: 10,
  division: '',
  issueCategory: '',
  jobStatus: '',
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { data, totalData, getAll, setSelectedId, clearState } = useRequestStore(
    state => ({
      data: state.data,
      totalData: state.totalData,
      getAll: state.getAll,
      setSelectedId: state.setSelectedId,
      clearState: state.clearState,
    }),
    shallow
  )
  const { profile } = useAuthStore(state => ({ profile: state.profile }), shallow)
  const { deleteSteelPlate } = useSteelPlatesStore(
    state => ({
      deleteSteelPlate: state.delete,
    }),
    shallow
  )
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(state => ({
    saleOrder: state.data,
    getAllSaleOrder: state.getAll,
  }))
  const {
    currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const [filter, setFilter] = useState(INITIAL_FILTER)

  const [mobileData, setMobileData] = useState<any[]>([])
  const [totalCountMobileData, setTotalCountMobileData] = useState(0)

  const userRoleId = profile?.roleId || 0

  const isHideWritingAction = [2, 15, 16, 20, 21].includes(userRoleId)

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const nextPageDesktop = (page: number) => {
    setFilter(prev => ({
      ...prev,
      page: page,
    }))

    updateLoading(true)
    getAll({
      ...filter,
      page: page,
      sortBy: currentSorting?.[0]?.id,
      sortOrder: currentSorting?.[0]?.desc ? 'desc' : 'asc',
    }).then(({ success }) => {
      if (!success) {
        updateLoading(false)
        updateError(true)
      } else {
        updateLoading(false)

        getAllSaleOrder({}).then(({ success }) => {
          if (!success) {
            updateLoading(false)
            updateError(true)
          } else {
            updateLoading(false)
          }
        })
      }
    })
  }

  const submitFilter = () => {
    fetchData(true)
  }

  const clearFilter = async () => {
    setFilter(INITIAL_FILTER)
    updateLoading(true)
    getAll(INITIAL_FILTER).then(({ success, data }) => {
      if (!success) {
        updateLoading(false)
        updateError(true)
      } else {
        updateLoading(false)

        getAllSaleOrder({}).then(({ success, data: saleOrderData }) => {
          if (!success) {
            updateLoading(false)
            updateError(true)
          } else {
            updateLoading(false)

            if (isMobile) {
              const { totalCount, data: responseData } = data

              const findLastPage = Math.ceil(totalCount / filter.pageSize)
              if (filter.page <= findLastPage) {
                const newData = responseData.map((item: any) => {
                  const code = saleOrderData.find((s: any) => s.id === item.saleOrderId)?.code

                  return {
                    ...item,
                    code,
                  }
                })

                setMobileData(newData)
              }

              setTotalCountMobileData(totalCount)
            }
          }
        })
      }
    })
  }

  const setupTable = () => {
    updateSorting('createdAt', true)
    updatePagination(true)
  }

  const fetchData = (isSubmitFilter?: boolean) => {
    updateLoading(true)
    getAll({
      ...filter,
      page: isSubmitFilter ? 1 : filter.page,
      sortBy: currentSorting?.[0]?.id,
      sortOrder: currentSorting?.[0]?.desc ? 'desc' : 'asc',
    }).then(({ success, data }) => {
      if (!success) {
        updateLoading(false)
        updateError(true)
      } else {
        onChangeFilter('page', data.page)
        updateLoading(false)

        getAllSaleOrder({}).then(({ success, data: saleOrderData }) => {
          if (!success) {
            updateLoading(false)
            updateError(true)
          } else {
            updateLoading(false)

            if (isMobile) {
              const { totalCount, data: responseData } = data

              const findLastPage = Math.ceil(totalCount / filter.pageSize)
              if (filter.page <= findLastPage) {
                const newData = responseData.map((item: any) => {
                  const code = saleOrderData.find((s: any) => s.id === item.saleOrderId)?.code

                  return {
                    ...item,
                    code,
                  }
                })

                if (isSubmitFilter) {
                  setMobileData(newData)
                } else {
                  setMobileData(prevState => [...prevState, ...newData])
                }
              }

              setTotalCountMobileData(totalCount)
            }
          }
        })
      }
    })
  }

  const createRequest = (issueType: IssueType) => {
    switch (issueType) {
      case IssueType.RequestSteelPlates: {
        navigate('/request/steel-plates/create')
        break
      }
      case IssueType.RequestChangePileSize: {
        navigate('/request/change-pile-size/create')
        break
      }
      case IssueType.RequestChangeShipping: {
        navigate('/request/change-shipping/create')
        break
      }
      default:
        return
    }
  }

  const editRequest = (id: string, issueType: IssueType) => {
    setSelectedId(parseInt(id))

    switch (issueType) {
      case IssueType.RequestSteelPlates: {
        navigate(`/request/steel-plates/${id}`)
        break
      }
      case IssueType.RequestChangePileSize: {
        navigate(`/request/change-pile-size/${id}`)
        break
      }
      case IssueType.RequestChangeShipping: {
        navigate(`/request/change-shipping/${id}`)
        break
      }
      default:
        return
    }
  }

  const viewRequest = (id: string, issueType: IssueType) => {
    setSelectedId(parseInt(id))

    switch (issueType) {
      case IssueType.RequestSteelPlates: {
        navigate(`/request/steel-plates/${id}?view=1`)
        break
      }
      case IssueType.RequestChangePileSize: {
        navigate(`/request/change-pile-size/${id}?view=1`)
        break
      }
      case IssueType.RequestChangeShipping: {
        navigate(`/request/change-shipping/${id}?view=1`)
        break
      }
      default:
        return
    }
  }

  const askDeleteRequest = async (id: string, issueType: IssueType) => {
    const { isConfirmed } = await Swal.fire({
      title: 'ลบคำร้องขอ',
      text: 'คุณต้องการที่จะลบคำร้องขอนี้หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      if (IssueType[issueType] === IssueType.RequestSteelPlates) {
        const { success, data } = await deleteSteelPlate(parseInt(id))
        if (success) {
          toast.success('ลบคำร้องขอสำเร็จ')
          fetchData()
        } else {
          toast.error(data)
        }
        return
      } else if (IssueType[issueType] === IssueType.RequestChangePileSize) {
      } else if (IssueType[issueType] === IssueType.RequestChangeShipping) {
      } else {
        toast.error('ไม่พบประเภทคำร้องขอที่ต้องการลบ')
        return
      }
    }
  }
  //////////////////
  // *Desktop View //
  //////////////////

  const TABLE_CONFIGS: any[] = [
    // {
    //   Header: 'No.',
    //   accessor: 'id',
    //   width: 50,
    //   minWidth: 50,
    //   Cell: ({ value }: any) => {
    //     return <span className='text-gray-800'>{value}</span>
    //   },
    // },
    {
      Header: 'วันที่สร้างรายการ',
      accessor: 'createdAt',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่ส่ง',
      accessor: 'requireDate',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่แจ้ง',
      accessor: 'requestedAt',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'Sale Order',
      accessor: 'saleOrderId',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value
              ? saleOrder.length > 0
                ? saleOrder.find(s => s.id === value)?.code
                : value
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'หน่วยงาน',
      accessor: 'division',
      width: 130,
      minWidth: 130,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'เรื่องที่ขอ',
      accessor: 'issueCategory',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        // @ts-ignore
        return <span className='text-gray-800'>{IssueTypeTranslated[value]}</span>
      },
    },
    {
      Header: 'สถานะงาน',
      accessor: 'jobStatus',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return <StatusCell status={value} />
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <CustomActionCell
          id={row.original.id}
          hideDuplicate
          hideEdit={isHideWritingAction}
          hideDelete={isHideWritingAction}
          onClickView={() => viewRequest(row.original.id, row.original.issueCategory as IssueType)}
          onClickEdit={() => editRequest(row.original.id, row.original.issueCategory as IssueType)}
          onClickDelete={() =>
            askDeleteRequest(row.original.id, row.original.issueCategory as IssueType)
          }
        />
      ),
      disableSortBy: true,
    },
  ]

  //////////////////
  // *Mobile View //
  //////////////////

  const nextPageMobile = () => {
    onChangeFilter('page', filter.page + 1)

    updateLoading(true)
    getAll({
      ...filter,
      page: filter.page + 1,
      sortBy: currentSorting?.[0]?.id,
      sortOrder: currentSorting?.[0]?.desc ? 'desc' : 'asc',
    }).then(({ success, data }) => {
      if (!success) {
        updateLoading(false)
        updateError(true)
      } else {
        updateLoading(false)

        getAllSaleOrder({}).then(({ success, data: saleOrderData }) => {
          if (!success) {
            updateLoading(false)
            updateError(true)
          } else {
            updateLoading(false)

            if (isMobile) {
              const { totalCount, data: responseData } = data

              const findLastPage = Math.ceil(totalCount / filter.pageSize)
              if (filter.page <= findLastPage) {
                const newData = responseData.map((item: any) => {
                  const code = saleOrderData.find((s: any) => s.id === item.saleOrderId)?.code
                  return {
                    ...item,
                    code,
                  }
                })
                setMobileData(prevState => [...prevState, ...newData])
              }

              setTotalCountMobileData(totalCount)
            }
          }
        })
      }
    })
  }

  // Initial Fetch and Initial Table Setup
  useEffect(() => {
    const action = async () => {
      setupTable()
    }

    action()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentSorting?.[0]?.id !== 'id') fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSorting])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
      clearState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isHideWritingAction,
    data,
    filter,
    onChangeFilter,
    submitFilter,
    clearFilter,
    createRequest,
    viewRequest,
    // Desktop View
    TABLE_CONFIGS,
    totalData,
    nextPageDesktop,
    // Mobile View
    mobileData,
    totalCountMobileData,
    nextPageMobile,
  }
}

export default ViewModel
